<script setup>
import { Link, Head, usePage } from '@inertiajs/vue3'
import { ref, computed, watch } from 'vue'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/vue'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import Paginator from 'primevue/paginator';
import moment from 'moment';
import axios from 'axios';

function formatMileage(mileage) {
  //iif mileage is null return empty string
  if (mileage === null) {
    return 0;
  }
  return mileage.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
}

let formatter = new Intl.NumberFormat('nl-NL', {
  style: 'decimal',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
});

const props = defineProps({ dealer: Object, bidsPlaced: Array })

const tabs = ref([
  { name: 'Op zoek naar', href: '#', count: props.dealer.searches.length, current: true },
  { name: 'Eerdere aankopen', href: '#', count: props.dealer.orders.length, current: false },
  { name: 'Eerdere biedingen', href: '#', count: props.bidsPlaced.length, current: false },
])

const selectTab = (selectedTab) => {
    tabs.value.forEach(tab => {
        tab.current = (tab === selectedTab)
    })
}

const deleteDialog = ref(false);

let deleteDialogText = "";
let deleteDialogUrl = "";

function openDeleteDialog(item) {
  deleteDialogText = item.company_name;
  deleteDialogUrl = "/portal/dealer/" + item.id + "/delete";
  deleteDialog.value = true;
}

const contactDialog = ref(false);

const page = usePage();

let eurFormatter = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const pageBids = computed(() => page.props.bidsPlaced);

const bids = ref(props.bidsPlaced);
const bidsPaginator = ref(null);
const paginatedBidsData = ref([]);

watch(pageBids, () => {
    bids.value = pageBids.value;
    //set paginator to page 1
    if (bidsPaginator.value) {
        bidsPaginator.value.changePage(0);
    }
})

watch(bids, () => {
    if (bidsPaginator.value) {
        bidsPaginator.value.changePage(0);
    }
    changeBidspagination({ rows: 10, page: 0 });
});

function changeBidspagination(pageState) {
    //get row count
    const rows = pageState.rows;
    //get page count
    const page = pageState.page;

    //calculate start index
    const startIndex = rows * page;
    //calculate end index
    const endIndex = startIndex + rows;
    //get the data for the current page

    paginatedBidsData.value = bids.value.slice(startIndex, endIndex);
}

changeBidspagination({ rows: 10, page: 0 });

function getRemainingTime(endDateTime) {
    const end = moment(endDateTime);

    const now = moment();

    const duration = moment.duration(end.diff(now));

    const days = duration.days();
    const hours = duration.hours();
    const minutes = duration.minutes();

    if (days > 0) {
        return `${days}d` + (hours > 0 ? ` ${hours}h` : '') + (minutes > 0 ? ` ${minutes}m` : '');
    } else if (hours > 0) {
        return `${hours}h` + (minutes > 0 ? ` ${minutes}m` : '');
    } else if (minutes > 0) {
        return `${minutes}m`;
    } else {
        return 'Veiling afgelopen';
    }
}

const pageOrders = computed(() => page.props.dealer.orders);

const orders = ref(props.dealer.orders);
const ordersPaginator = ref(null);
const paginatedOrdersData = ref([]);

watch(pageOrders, () => {
  orders.value = pageOrders.value;
    //set paginator to page 1
    if (ordersPaginator.value) {
        ordersPaginator.value.changePage(0);
    }
})

watch(orders, () => {
    if (ordersPaginator.value) {
        ordersPaginator.value.changePage(0);
    }
    changeOrdersPagination({ rows: 10, page: 0 });
});

function changeOrdersPagination(pageState) {
    //get row count
    const rows = pageState.rows;
    //get page count
    const page = pageState.page;

    //calculate start index
    const startIndex = rows * page;
    //calculate end index
    const endIndex = startIndex + rows;
    //get the data for the current page

    paginatedOrdersData.value = orders.value.slice(startIndex, endIndex);
}

changeOrdersPagination({ rows: 10, page: 0 });


const pageComments = computed(() => page.props.dealer.notes);

const comments = ref(props.dealer.notes);
const commentsPaginator = ref(null);
const paginatedCommentsData = ref([]);

watch(pageComments, () => {
  comments.value = pageComments.value;
    //set paginator to page 1
    if (commentsPaginator.value) {
        commentsPaginator.value.changePage(0);
    }
})

watch(comments, () => {
    if (commentsPaginator.value) {
        commentsPaginator.value.changePage(0);
    }
    changeCommentsPagination({ rows: 5, page: 0 });
});

function changeCommentsPagination(pageState) {
    //get row count
    const rows = pageState.rows;
    //get page count
    const page = pageState.page;

    //calculate start index
    const startIndex = rows * page;
    //calculate end index
    const endIndex = startIndex + rows;
    //get the data for the current page

    paginatedCommentsData.value = comments.value.slice(startIndex, endIndex);
}

changeCommentsPagination({ rows: 5, page: 0 });

const deleteCommentModal = ref(false);
const deletingComment = ref(null);

function openCommentDeleteModal(comment) {
  deleteCommentModal.value = true;
  deletingComment.value = comment;
}

function closeCommentDeleteModal() {
  deleteCommentModal.value = false;
}

function deleteComment(comment) {
  axios.delete('/portal/dealer/' + props.dealer.id + '/comment/' + deletingComment.value.id + '/delete').then(response => {
    deleteCommentModal.value = false;
    comments.value = comments.value.filter(item => item.id !== deletingComment.value.id);
  });
}

const comment = ref('');

function submitComment() {
  if (!comment.value) return;
  axios.post('/portal/dealer/' + props.dealer.id + '/comment', {
    comment: comment.value,
  }).then(response => {
    comment.value = null;
    //add new comment to comments array
    comments.value = response.data.comments;
  });
}

const pageSearches = computed(() => page.props.dealer.searches);

const searches = ref(props.dealer.searches);
const searchPaginator = ref(null);
const paginatedSearchData = ref([]);

watch(pageSearches, () => {
  searches.value = pageSearches.value;
    //set paginator to page 1
    if (searchPaginator.value) {
        searchPaginator.value.changePage(0);
    }
})

watch(searches, () => {
    if (searchPaginator.value) {
        searchPaginator.value.changePage(0);
    }
    changeSearchPagination({ rows: 10, page: 0 });
});

function changeSearchPagination(pageState) {
    //get row count
    const rows = pageState.rows;
    //get page count
    const page = pageState.page;

    //calculate start index
    const startIndex = rows * page;
    //calculate end index
    const endIndex = startIndex + rows;
    //get the data for the current page

    paginatedSearchData.value = searches.value.slice(startIndex, endIndex);
}

changeSearchPagination({ rows: 10, page: 0 });

</script>
<template>

  <Head title="Dealer bekijken" />
  <div class="relative">
    <div class="absolute bottom-4 right-4 gap-4 flex">
      <Link :href="'/portal/dealer/' + dealer.id + '/edit'" type="button"
        class="inline-flex justify-center rounded-md bg-white px-2 md:px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 gap-x-2">
        <font-awesome-icon :icon="['far', 'pencil']" class="h-5 w-5 text-gray-400 shrink-0"/>
        <span class="hidden md:block">Aanpassen</span>
      </Link>
      <button @click="openDeleteDialog(dealer)" type="button"
        class="inline-flex justify-center rounded-md bg-white px-2 md:px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 gap-x-2">
        <font-awesome-icon :icon="['far', 'trash-can']" class="h-5 w-5 text-gray-400 shrink-0"/>
        <span class="hidden md:block">Verwijderen</span>
      </button>
      <button @click="contactDialog = true" type="button"
        class="inline-flex justify-center rounded-md bg-white px-2 md:px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 gap-x-2">
        <font-awesome-icon :icon="['far', 'phone']" class="h-5 w-5 text-gray-400 shrink-0"/>
        <span class="hidden md:block">Contact</span>
      </button>
      <a type="button" :href="'https://' + dealer.website" target="_blank"
        class="inline-flex justify-center rounded-md bg-white px-2 md:px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 gap-x-2">
        <font-awesome-icon :icon="['far', 'globe']" class="h-5 w-5 text-gray-400 shrink-0"/>
        <span class="hidden md:block">Website</span>
      </a>
    </div>
    <img class="h-24 md:h-32 w-full object-cover lg:h-48" :src="'https://images.unsplash.com/photo-1444628838545-ac4016a5418a?ixid=MXwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHw%3D&ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'" alt="" />
  </div>
  <main class="px-4 sm:px-6 pb-6 space-y-8">
    <div class="-mt-20 sm:-mt-18 flex items-end space-x-5">
      <div class="flex flex-col flex-1 mt-24">
        <div>
          <nav class="sm:hidden" aria-label="Back">
            <Link href="/portal/dealers" class="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 gap-x-1">
                <font-awesome-icon :icon="['fas', 'chevron-left']" class="h-3 w-3 text-gray-400 shrink-0" />
                Terug
            </Link>
          </nav>
          <nav class="hidden sm:flex" aria-label="Breadcrumb">
            <ol role="list" class="flex items-center space-x-4">
              <li>
                <div class="flex">
                  <Link href="/portal/dashboard" class="text-sm font-medium text-gray-500 hover:text-gray-700">Dashboard
                  </Link>
                </div>
              </li>
              <font-awesome-icon :icon="['fas', 'chevron-right']" class="h-3 w-3 text-gray-400 shrink-0" />
              <li>
                <div class="flex">
                  <Link href="/portal/dealers" class="text-sm font-medium text-gray-500 hover:text-gray-700">Dealers
                  </Link>
                </div>
              </li>
              <font-awesome-icon :icon="['fas', 'chevron-right']" class="h-3 w-3 text-gray-400 shrink-0" />
              <li>
                <div class="flex items-center">
                  <Link href="#" class=" text-sm font-medium text-gray-900 hover:text-gray-700">
                  {{ dealer.company_name }}</Link>
                </div>
              </li>
            </ol>
          </nav>
        </div>
        <div class="sm:flex sm:min-w-0 sm:flex-1 sm:items-center sm:space-x-6 sm:pb-1 mt-2">
          <h1 class="truncate text-2xl font-bold text-gray-900">{{ dealer.full_name }}</h1>
        </div>
      </div>
    </div>
    <div class="hidden min-w-0 flex-1 sm:block md:hidden">
      <h1 class="truncate text-2xl font-bold text-gray-900">{{ dealer.full_name }}</h1>
    </div>
    <div class="flex flex-col lg:flex-row w-full gap-6 pt-6">
      <div class="w-full ring-1 p-4 rounded-lg shadow-md flex flex-col gap-6 lg:w-3/5">
        <div>
          <h3 class="text-lg font-semibold leading-7 text-gray-900">Informatie</h3>
          <p class="mt-4 text-sm leading-6 text-gray-500"
            v-text="props.dealer.description ? props.dealer.description : 'Er is geen informatie voor deze dealer opgegeven'">
          </p>
        </div>
        <div class="border-y border-gray-100 grid grid-cols-1 md:grid-cols-2 divide-y divide-gray-100">
          <div class="flex flex-ro md:flex-col justify-between py-4">
            <dt class="text-sm font-medium leading-6 text-gray-900">Contact persoon</dt>
            <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0" v-text="props.dealer.full_name"></dd>
          </div>
          <div class="flex flex-ro md:flex-col justify-between py-4">
            <dt class="text-sm font-medium leading-6 text-gray-900">Email address</dt>
            <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0" v-text="props.dealer.email"></dd>
          </div>
          <div class="flex flex-ro md:flex-col justify-between py-4">
            <dt class="text-sm font-medium leading-6 text-gray-900">Telefoon nummer (Whatsapp / 06)</dt>
            <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
              <a :href="'https://wa.me/'+props.dealer.whatsapp_number"
                class="text-royalblue-500 hover:text-royalblue-700">{{ props.dealer.whatsapp_number }}</a>
            </dd>
          </div>
          <div class="flex flex-ro md:flex-col justify-between py-4">
            <dt class="text-sm font-medium leading-6 text-gray-900">Telefoon nummer (vaste lijn)</dt>
            <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0"> <a :href="'tel:'+props.dealer.phone_number"
                class="text-royalblue-500 hover:text-royalblue-700">{{ props.dealer.phone_number }}</a>
            </dd>
          </div>
          <div class="flex flex-ro md:flex-col justify-between py-4">
            <dt class="text-sm font-medium leading-6 text-gray-900">Website</dt>
            <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
              v-text="'https://' + props.dealer.website"></dd>
          </div>
          <div class="flex flex-ro md:flex-col justify-between py-4">
            <dt class="text-sm font-medium leading-6 text-gray-900">Adres</dt>
            <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0" v-text="props.dealer.full_address">
            </dd>
          </div>
        </div>
        <div class="grid grid-cols-1 gap-4 sm:gap-6 md:gap-8">
          <div>
            <dt class="text-lg font-semibold leading-7 text-gray-900">Favoriete merken</dt>
            <li class="flex flex-wrap gap-2.5 py-4 text-sm leading-6">
              <div v-for="brand in props.dealer.brands" v-if="props.dealer.brands.length > 0"
                class="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white">
                <span class="truncate font-medium p-2">{{ brand.name }}</span>
              </div>
              <div v-else>
                <span class="text-gray-500">Geen favoriete merken</span>
              </div>
            </li>
          </div>
          <div>
            <dt class="text-lg font-semibold leading-7 text-gray-900">Categorieën</dt>
            <li class="flex flex-wrap gap-2.5 py-4 text-sm leading-6">
              <div v-for="category in props.dealer.categories" v-if="props.dealer.categories.length > 0"
                class="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white">
                <span class="truncate font-medium p-2">{{ category.name }}</span>
              </div>
              <div v-else>
                <span class="text-gray-500">Geen categorieën</span>
              </div>
            </li>
          </div>
        </div>
      </div>
      <div class="w-full flex flex-col lg:w-2/5">
        <div class="w-full ring-1 p-4 rounded-lg shadow-md space-y-6">
          <h3 class="text-lg font-semibold leading-7 text-gray-900">Opmerkingen</h3>
          <div class="flex flex-row items-center gap-2" v-for="comment in paginatedCommentsData" v-if="dealer.notes.length > 0">
            <img class="rounded-full h-16 w-16" :src="'/storage/avatars/'+comment.placed_by.avatar" alt="">
            <div class="flex flex-col w-full">
              <div class="flex flex-row justify-between">
                <p class="font-bold">{{comment.placed_by.full_name}}</p>
                <p>{{moment(comment.created_at).format('DD-MM-YYYY HH:mm')}}</p>
              </div>
              <p>{{comment.note}}</p>
              <p class="text-balance flex">
                <span class="flex-grow">
                  
                </span>
                <span class="justify-end">
                  <font-awesome-icon @click="openCommentDeleteModal(comment)" :icon="['far', 'trash-can']" class="h-5 w-5 shrink-0 p-2.5 shadow-sm ring-1 ring-inset ring-gray-300 bg-white hover:bg-gray-50 rounded-lg cursor-pointer"/>
                </span>
              </p>
            </div>
          </div>
          <div v-else>
            <p class="text-gray-500">Geen opmerkingen</p>
          </div>
          
          <div class="mt-5">
            <textarea rows="5" name="comment" id="comment" v-model="comment"
              class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-royalblue-500 sm:text-sm sm:leading-6"
              placeholder="Plaats hier je opmerking..." />
          </div>
          <div class="mt-2 flex justify-end">
            <button @click="submitComment"
              class="inline-flex items-center rounded-md bg-brightorange px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brightorange/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-royalblue-500">Reactie
              plaatsen</button>
          </div>
        </div>
            <Paginator ref="commentsPaginator" v-if="comments.length > 5" @page="changeCommentsPagination" :rows="5"
                :totalRecords="comments.length" :rowsPerPageOptions="[5]"></Paginator>
        
      </div>
    </div>
    
    <TabGroup>
      <div class="flex flex-row justify-between overflow-x-auto overflow-y-clip">
        <TabList class="-mb-px flex space-x-4" aria-label="Tabs">
          <Tab v-for="tab in tabs" :key="tab.name" href="#"
            :class="[tab.current ? 'border-brightorange text-brightorange' : 'border-transparent text-gray-500 hover:border-gray-200 hover:text-gray-700', 'flex whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium']"
            :aria-current="tab.current ? 'page' : undefined" @click.prevent="selectTab(tab)">
            {{ tab.name }}
            <span v-if="tab.count"
              :class="[tab.current ? 'bg-brightorange text-white' : 'bg-gray-100 text-gray-900', 'ml-3 hidden rounded-full py-0.5 px-2.5 text-xs font-medium lg:inline-block']">{{
                tab.count }}</span>
          </Tab>
        </TabList>
      </div>
      <TabPanels class="block overflow-x-auto overflow-y-clip sm:overflow-visible">
        <TabPanel class="space-y-8">
          
          <div class="space-y-8" v-if="searches.length > 0">
            <div class="mt-10 ring-1 ring-gray-300 mx-0 rounded-lg">
                <table class="min-w-full divide-y divide-gray-300 border-b">
                    <thead>
                        <tr>
                            <th scope="col"
                                class="hidden md:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Merk
                            </th>
                            <th scope="col"
                                class="hidden sm:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Model
                            </th>
                            <th scope="col"
                                class="table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Prijs
                            </th>
                            <th scope="col"
                                class="table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                KM stand
                            </th>
                            <th scope="col"
                                class="hidden sm:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Bouwjaar
                            </th>
                            <th scope="col"
                                class="hidden xl:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Vermogen
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y">
                        <tr v-for="search in paginatedSearchData">
                            <td class="whitespace-nowrap hidden md:table-cell px-3 py-4 text-sm text-gray-500">
                                {{search.make}}
                            </td>
                            <td class="whitespace-nowrap hidden sm:table-cell px-3 py-4 text-sm text-gray-500">
                                {{search.model}}
                            </td>
                            <td class="whitespace-nowrap table-cell px-3 py-4 text-sm text-gray-500 max-w-32 sm:max-w-40 overflow-clip overflow-ellipsis">
                                {{ search.min_price ? eurFormatter.format(search.min_price) : 'Geen minimum prijs' }} - {{ search.max_price ? eurFormatter.format(search.max_price) : 'Geen maximum prijs' }}
                            </td>
                            <td class="whitespace-nowrap table-cell px-3 py-4 text-sm text-gray-500">
                                {{ search.min_mileage ? formatMileage(search.min_mileage) : 'Geen minimum KM stand' }} - {{ search.max_mileage ? formatMileage(search.max_mileage) : 'Geen maximum KM stand' }}    
                            </td>
                            <td class="whitespace-nowrap hidden sm:table-cell px-3 py-4 text-sm text-gray-500">
                                {{ search.min_year ? search.min_year : 'Geen minimum bouwjaar' }} - {{ search.max_year ? search.max_year : 'Geen maximum bouwjaar' }}
                            </td>
                            <td class="whitespace-nowrap hidden xl:table-cell px-3 py-4 text-sm text-gray-500">
                                {{ search.min_power ? search.min_power : 'Geen minimum vermogen' }} - {{ search.max_power ? search.max_power : 'Geen maximum vermogen' }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            <Paginator ref="searchPaginator" v-if="searches.length > 10" @page="changeSearchPagination" :rows="10"
                :totalRecords="searches.length" :rowsPerPageOptions="[10, 20, 30]"></Paginator>
            </div>
          </div>
          <div v-else>
              <div class="flex flex-col items-center justify-center">
                  <font-awesome-icon :icon="['far', 'magnifying-glass']" class="h-12 w-12 text-gray-400 shrink-0"/>
                  <p class="mt-4 text-sm font-semibold text-gray-900">Geen zoekopdrachten gevonden</p>
              </div>
          </div>

        </TabPanel>
        <TabPanel class="space-y-8">
          <div class="space-y-8" v-if="orders.length > 0">
            <div class="mt-10 ring-1 ring-gray-300 mx-0 rounded-lg">
                <table class="min-w-full divide-y divide-gray-300 border-b">
                    <thead>
                        <tr>
                            <th scope="col"
                                class="hidden md:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Kavelnummer
                            </th>
                            <th scope="col"
                                class="hidden md:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Merk
                            </th>
                            <th scope="col"
                                class="hidden md:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Model
                            </th>
                            <th scope="col"
                                class="hidden md:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Bouwjaar
                            </th>
                            <th scope="col"
                                class="hidden md:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Kilometerstand
                            </th>
                            <th scope="col"
                                class="hidden sm:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Betaald bedrag
                            </th>
                            <th scope="col"
                                class="table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Fee
                            </th>
                            <th scope="col"
                                class="table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Extra prijs
                            </th>
                            <th scope="col"
                                class="table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Korting
                            </th>
                            <th scope="col"
                                class="hidden sm:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Status
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y">
                        <tr v-for="order in paginatedOrdersData">
                            <td class="whitespace-nowrap hidden md:table-cell px-3 py-4 text-sm text-gray-500">
                              {{order.lot_id}}
                            </td>
                            <td class="whitespace-nowrap hidden md:table-cell px-3 py-4 text-sm text-gray-500">
                              {{order.make}}
                            </td>
                            <td class="whitespace-nowrap hidden md:table-cell px-3 py-4 text-sm text-gray-500">
                              {{order.model}}
                            </td>
                            <td class="whitespace-nowrap hidden md:table-cell px-3 py-4 text-sm text-gray-500">
                              {{order.construction_year}}
                            </td>
                            <td class="whitespace-nowrap hidden md:table-cell px-3 py-4 text-sm text-gray-500">
                              {{ formatter.format(lot.mileage) }} km
                            </td>
                            <td class="whitespace-nowrap hidden sm:table-cell px-3 py-4 text-sm text-gray-500">
                              {{eurFormatter.format(order.sold_price)}}
                            </td>
                            <td class="whitespace-nowrap table-cell px-3 py-4 text-sm text-gray-500 max-w-32 sm:max-w-40 overflow-clip overflow-ellipsis">
                              {{eurFormatter.format(order.fee)}}
                            </td>
                            <td class="whitespace-nowrap table-cell px-3 py-4 text-sm text-gray-500">
                              {{ eurFormatter.format(order.extra_price) }}    
                            </td>
                            <td class="whitespace-nowrap table-cell px-3 py-4 text-sm text-gray-500">
                              {{ eurFormatter.format(order.discount) }}    
                            </td>
                            <td class="whitespace-nowrap hidden lg:table-cell px-3 py-4 text-sm text-gray-500">
                                {{ order.status }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            <Paginator ref="ordersPaginator" v-if="orders.length > 10" @page="changeOrdersPagination" :rows="10"
                :totalRecords="orders.length" :rowsPerPageOptions="[10, 20, 30]"></Paginator>
            </div>
          </div>
            <div v-else>
                <div class="flex flex-col items-center justify-center">
                  <font-awesome-icon :icon="['far', 'square-parking']" class="h-12 w-12 text-gray-400 shrink-0"/>
                    <p class="mt-4 text-sm font-semibold text-gray-900">Geen eerdere aankopen gevonden</p>
                </div>
            </div>
        </TabPanel>
        <TabPanel class="space-y-8">
          <div class="space-y-8" v-if="bids.length > 0">
            <div class="mt-10 ring-1 ring-gray-300 mx-0 rounded-lg">
                <table class="min-w-full divide-y divide-gray-300 border-b">
                    <thead>
                        <tr>
                            <th scope="col"
                                class="hidden md:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Kavelnummer
                            </th>
                            <th scope="col"
                                class="hidden sm:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Loopt af over
                            </th>
                            <th scope="col"
                                class="table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Kavel
                            </th>
                            <th scope="col"
                                class="table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Geplaatst bod
                            </th>
                            <th scope="col"
                                class="hidden sm:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Hoogste bod
                            </th>
                            <th scope="col"
                                class="hidden lg:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Indicatieprijs
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y">
                        <tr v-for="bid in paginatedBidsData">
                            <td class="whitespace-nowrap hidden md:table-cell px-3 py-4 text-sm text-gray-500">
                                {{bid.lot_id}}
                            </td>
                            <td class="whitespace-nowrap hidden sm:table-cell px-3 py-4 text-sm text-gray-500">
                                {{getRemainingTime(bid.lot.auction_end_date)}}
                            </td>
                            <td class="whitespace-nowrap table-cell px-3 py-4 text-sm text-gray-500 max-w-32 sm:max-w-40 overflow-clip overflow-ellipsis">
                                {{bid.lot.make}} {{bid.lot.model}} {{bid.lot.construction_year}}
                            </td>
                            <td class="whitespace-nowrap table-cell px-3 py-4 text-sm text-red-500">
                                {{eurFormatter.format(bid.amount)}}</td>
                            <td class="whitespace-nowrap hidden sm:table-cell px-3 py-4 text-sm text-green-500">
                                {{eurFormatter.format(bid.lot.highest_bid)}} </td>
                            <td class="whitespace-nowrap hidden lg:table-cell px-3 py-4 text-sm text-gray-500">
                                {{eurFormatter.format(bid.lot.price_indication)}}
                            </td>
                            <td
                                class="hidden lg:flex whitespace-nowrap py-4 pr-4 pl-3 text-right text-sm font-medium gap-1.5">
                                <Link :href="'/portal/kavel/'+bid.lot_id" class="text-royalblue-500 hover:text-royalblue-500/80 ml-auto">
                                Bekijk kavel</Link>
                            </td>
                            <td class="flex lg:hidden whitespace-nowrap py-4 text-sm gap-1.5 pr-3">
                                <Link href="" class="text-royalblue-500 hover:text-royalblue-500/80 ml-auto">
                                <font-awesome-icon :icon="['far', 'eye']" class="text-royalblue-500 hover:text-royalblue-500/80 h-5 w-5 shrink-0"/>

                                </Link>
                            </td>
                        </tr>
                    </tbody>
                </table>
            <Paginator ref="bidsPaginator" v-if="bids.length > 10" @page="changeBidsPagination" :rows="10"
                :totalRecords="bids.length" :rowsPerPageOptions="[10, 20, 30]"></Paginator>
            </div>
          </div>
          <div v-else>
              <div class="flex flex-col items-center justify-center">
                <font-awesome-icon :icon="['far', 'gavel']" class="h-12 w-12 text-gray-400 shrink-0"/>
                  <p class="mt-4 text-sm font-semibold text-gray-900">Geen biedingen gevonden</p>
              </div>
          </div>
        </TabPanel>
      </TabPanels>
    </TabGroup>
  </main>

  <TransitionRoot as="template" :show="deleteDialog">
    <Dialog as="div" class="relative z-40" @close="deleteDialog = false">
      <span class="sr-only">Delete dialog</span>
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
        leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed left-0 right-0 top-0 bottom-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed left-0 right-0 top-0 bottom-0 z-40 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div class="sm:flex sm:items-start">
                <div
                  class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <font-awesome-icon :icon="['fal', 'triangle-exclamation']" class="h-6 w-6 text-red-500 shrink-0" />
                </div>
                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Dealer verwijderen
                  </DialogTitle>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500">Je staat op het punt om dealer <span
                        v-html="deleteDialogText"></span> te verwijderen. Weet je dit zeker?</p>
                  </div>
                </div>
              </div>
              <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <Link preserve-scroll method="delete" :href="deleteDialogUrl" type="button"
                  class="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                  @click="deleteDialog = false" as="button">Verwijderen</Link>
                <button type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  @click="deleteDialog = false" ref="cancelButtonRef">Annuleren</button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <TransitionRoot as="template" :show="contactDialog">
    <Dialog as="div" class="relative z-50" @close="contactDialog = false">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
        leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 transition-opacity" @click="contactDialog = false" />
      </TransitionChild>

      <div class="fixed left-0 right-0 top-0 bottom-0 z-[100] w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
              <div class="flex justify-between items-center pb-4 mb-4 rounded-t border-b sm:mb-5 ">
                <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Contact
                </DialogTitle>
                <button type="button"
                  class="text-gray-900 hover:text-royalblue-500 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  @click="contactDialog = false">
                            <font-awesome-icon :icon="['far', 'xmark']" class="h-5 w-5 shrink-0"/>

                  <span class="sr-only">Close modal</span>
                </button>
              </div>
              <form action="#">
                <div class="mb-4">
                  <label for="category" class="block mb-2 text-sm font-medium text-gray-900">Neem contact op met de
                    dealer</label>
                  <div class="flex flex-col gap-2 mb-6">
                    <div class="flex items-center justify-between">
                      <div class="flex gap-2">
                        <p class="text-sm text-gray-500 w-32">Telefoonnummer:</p>
                        <p class="text-sm text-gray-500">{{ dealer.phone_number }}</p>
                      </div>
                      <div class="flex justify-end">
                        <a type="button" :href="'tel:' + dealer.phone_number"
                          class="mt-3 gap-4 inline-flex w-auto justify-between rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                          ref="">
                          <font-awesome-icon :icon="['fas', 'phone']" class="h-5 w-5 shrink-0" />
                          <p class="w-10 text-left">Bel</p>
                        </a>
                      </div>
                    </div>
                    <div class="flex items-center justify-between">
                      <div class="flex gap-2">
                        <p class="text-sm text-gray-500 w-32">E-mail:</p>
                        <p class="text-sm text-gray-500">{{ dealer.email }}</p>
                      </div>
                      <div class="flex justify-end">
                        <a type="button" :href="'mailto:' + dealer.email"
                          class="mt-3 gap-4 inline-flex w-auto justify-between rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                          ref="">
                          <font-awesome-icon :icon="['far', 'envelope']" class="h-5 w-5 shrink-0"/>
                          <p class="w-10 text-left">Mail</p>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <TransitionRoot as="template" :show="deleteCommentModal">
    <Dialog as="div" class="relative z-40" @close="closeCommentDeleteModal">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
        leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed left-0 right-0 top-0 bottom-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed left-0 right-0 top-0 bottom-0 z-40 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div class="sm:flex sm:items-start">
                <div
                  class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <font-awesome-icon :icon="['fal', 'triangle-exclamation']" class="h-6 w-6 text-red-500 shrink-0" />
                </div>
                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Opmerking verwijderen
                  </DialogTitle>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500">Je staat op het punt een opmerking te verwijderen. Weet je dit zeker?
                    </p>
                  </div>
                </div>
              </div>
              <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button preserve-scroll type="button" @click="deleteComment"
                  class="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                  as="button">Verwijderen</button>
                <button type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  @click="closeCommentDeleteModal" ref="cancelButtonRef">Annuleren</button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
